import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { AGDataGrid } from '@/components/ui/ag-data-grid';
import { calculateNominalAPR } from '@prolarus/shared';
import { format, parseISO } from 'date-fns';
import type { Status } from '@/services';

interface Invoice {
  id: string;
  vendor: { name: string } | null;
  invoice_number: string | null;
  total_amount: number | null;
  discount: number | null;
  discount_amt: number | null;
  discount_expiration: string | null;
  due_date: string | null;
  status_id: string | null;
  current_status: { id: string; name: string } | null;
}

interface EarlyPayTableProps {
  invoices: Invoice[];
  statuses: Status[];
  selectedStatus: string | null;
  onStatusChange: (value: string) => void;
}

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

export function EarlyPayTable({ invoices, statuses, selectedStatus, onStatusChange }: EarlyPayTableProps) {
  const navigate = useNavigate();

  const columnDefs = useMemo<ColDef<Invoice>[]>(() => [
    {
      field: 'invoice_number',
      headerName: 'Invoice',
      flex: 1,
      cellClass: 'font-medium',
      valueFormatter: (p) => p.value || '—',
    },
    {
      headerName: 'Vendor',
      flex: 1.5,
      valueGetter: (params) => params.data?.vendor?.name,
      valueFormatter: (p) => p.value || '—',
    },
    {
      headerName: 'Status',
      width: 140,
      valueGetter: (params) => params.data?.current_status?.name,
      cellRenderer: (params: ICellRendererParams<Invoice>) =>
        params.data?.current_status ? (
          <span className="badge badge-primary">
            {params.data.current_status.name}
          </span>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      field: 'discount_expiration',
      headerName: 'Expiration',
      flex: 1,
      sort: 'asc',
      valueFormatter: (p) => {
        if (!p.value) return '—';
        try {
          return format(parseISO(p.value), 'MMM d, yyyy');
        } catch {
          return '—';
        }
      },
    },
    {
      field: 'discount_amt',
      headerName: 'Discount Amt',
      type: 'rightAligned',
      flex: 1,
      valueFormatter: (p) => p.value != null ? currencyFormatter.format(p.value) : '—',
    },
    {
      headerName: 'Nominal APR',
      type: 'rightAligned',
      flex: 1,
      valueGetter: (params) => {
        const apr = calculateNominalAPR({
          discount: params.data?.discount,
          dueDate: params.data?.due_date,
          discountExpiration: params.data?.discount_expiration,
        });
        return apr?.value ?? null;
      },
      valueFormatter: (p) => {
        if (p.value == null) return '—';
        return `${p.value.toFixed(1)}%`;
      },
    },
    {
      field: 'total_amount',
      headerName: 'Total',
      type: 'rightAligned',
      flex: 1,
      valueFormatter: (p) => p.value != null ? currencyFormatter.format(p.value) : '—',
    },
  ], []);

  const toolbarActions = (
    <Select value={selectedStatus || ''} onValueChange={onStatusChange}>
      <SelectTrigger className="w-[160px]">
        <SelectValue placeholder="Select Status" />
      </SelectTrigger>
      <SelectContent>
        {statuses.map((status) => (
          <SelectItem key={status.id} value={status.id}>
            {status.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  return (
    <div>
      <h3 className="text-lg font-semibold mb-3">Invoices with Available Discounts</h3>
      <AGDataGrid<Invoice>
      rowData={invoices}
      columnDefs={columnDefs}
      totalCount={invoices.length}
      pageIndex={0}
      pageSize={100}
      searchPlaceholder="Search invoices..."
      rowSelection={false}
      height="calc(100vh - 580px)"
      toolbarActions={toolbarActions}
      onRowClicked={(data) => navigate(`/invoices/${data.id}`)}
    />
    </div>
  );
}
